/** Kiosko del Casino Lomas Bayas — pantalla pública para verificar acceso y ver últimos ingresos. */
import React, { useState, useRef, useEffect, useCallback } from 'react';
import axios from 'axios';
import { CheckCircle2, XCircle, Clock } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

// Imagen genérica de comida (bandeja / plato) — Unsplash pública, sin auth.
const FOOD_BG = 'https://images.unsplash.com/photo-1546069901-ba9599a7e63c?auto=format&fit=crop&w=1600&q=60';

const COLOR_HEX = {
  emerald: '#10b981', amber: '#f59e0b', lime: '#84cc16', sky: '#0ea5e9',
  indigo: '#6366f1', violet: '#8b5cf6', rose: '#f43f5e', orange: '#f97316',
};

export default function SgCasinoKiosko() {
  const [rut, setRut] = useState('');
  const [result, setResult] = useState(null);
  const [state, setState] = useState({ servicio_actual: null, ultimos_usos: [] });
  const [logoUrl, setLogoUrl] = useState(() => localStorage.getItem('cached_logo_url'));
  const [ahora, setAhora] = useState(new Date());
  const inputRef = useRef(null);
  const timerRef = useRef(null);

  // Cargar logo
  useEffect(() => {
    if (!logoUrl) {
      axios.get(`${API}/settings/logo`).then(r => {
        if (r.data?.url) { setLogoUrl(r.data.url); localStorage.setItem('cached_logo_url', r.data.url); }
      }).catch(() => {});
    }
  }, [logoUrl]);

  // Focus permanente al input
  useEffect(() => { inputRef.current?.focus(); }, []);

  // Mantener focus en el input siempre — click en cualquier parte devuelve foco.
  useEffect(() => {
    const refocus = () => {
      // Sólo refocus si no estamos mostrando resultado (que oculta el input)
      if (!result && inputRef.current) inputRef.current.focus();
    };
    window.addEventListener('click', refocus);
    window.addEventListener('focus', refocus);
    const id = setInterval(refocus, 1500);
    return () => {
      window.removeEventListener('click', refocus);
      window.removeEventListener('focus', refocus);
      clearInterval(id);
    };
  }, [result]);

  // Reloj en tiempo real
  useEffect(() => {
    const id = setInterval(() => setAhora(new Date()), 1000);
    return () => clearInterval(id);
  }, []);

  // Poll estado kiosko cada 15s
  const loadState = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/servicios-generales/casino/kiosko-state`, { params: { limit: 5 } });
      setState(r.data);
    } catch { /* silencioso */ }
  }, []);
  useEffect(() => {
    loadState();
    const id = setInterval(loadState, 15000);
    return () => clearInterval(id);
  }, [loadState]);

  // Auto-reset después de 8s
  useEffect(() => {
    if (result) {
      timerRef.current = setTimeout(() => {
        setResult(null); setRut(''); inputRef.current?.focus();
      }, 8000);
      return () => clearTimeout(timerRef.current);
    }
  }, [result]);

  const submit = async (e) => {
    e?.preventDefault();
    if (!rut.trim()) return;
    try {
      const r = await axios.post(`${API}/servicios-generales/casino/kiosko-registrar`, { rut });
      setResult({ ...r.data, ts: new Date() });
      loadState();
    } catch (err) {
      setResult({ ok: false, motivo: 'Error de conexión' });
    }
  };

  const servicio = state.servicio_actual;
  const authorized = result?.ok === true;

  return (
    <div className="min-h-screen relative overflow-hidden bg-slate-900">
      {/* Background con imagen de comida + overlay oscuro */}
      <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url(${FOOD_BG})` }} />
      <div className="absolute inset-0 bg-gradient-to-br from-slate-900/95 via-slate-900/85 to-emerald-950/95" />

      {/* HEADER */}
      <header className="relative z-10 flex items-center justify-between px-8 pt-6 pb-4">
        <div className="flex items-center gap-4">
          {logoUrl ? (
            <img src={logoUrl} alt="Lomas Bayas" className="h-14 w-auto brightness-0 invert" />
          ) : (
            <div className="text-white font-black text-2xl tracking-tight">LOMAS BAYAS</div>
          )}
          <div className="border-l border-white/20 pl-4">
            <div className="text-[10px] font-bold uppercase tracking-widest text-emerald-300">Servicios Generales</div>
            <div className="text-2xl font-black text-white leading-tight">Casino</div>
          </div>
        </div>
        <div className="text-right text-white">
          <div className="text-4xl font-black tabular-nums leading-none">
            {ahora.toLocaleTimeString('es-CL', { hour: '2-digit', minute: '2-digit' })}
          </div>
          <div className="text-[11px] uppercase tracking-widest opacity-70 mt-1">
            {ahora.toLocaleDateString('es-CL', { weekday: 'long', day: '2-digit', month: 'long' })}
          </div>
        </div>
      </header>

      {/* Servicio actual banner */}
      {servicio && (
        <div className="relative z-10 mx-8 mb-4 rounded-2xl overflow-hidden shadow-2xl"
             style={{ background: `linear-gradient(90deg, ${COLOR_HEX[servicio.color] || '#10b981'}, ${COLOR_HEX[servicio.color] || '#10b981'}dd)` }}>
          <div className="px-6 py-4 flex items-center justify-between text-white">
            <div>
              <div className="text-[10px] font-bold uppercase tracking-widest opacity-90">Servicio en curso</div>
              <div className="text-3xl font-black">{servicio.nombre}</div>
            </div>
            <div className="flex items-center gap-6">
              <div>
                <div className="text-[10px] font-bold uppercase tracking-widest opacity-90 flex items-center gap-1"><Clock className="w-3 h-3" /> Horario</div>
                <div className="text-xl font-bold font-mono">{servicio.hora_inicio} — {servicio.hora_fin}</div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* CONTENIDO PRINCIPAL — 2 columnas */}
      <div className="relative z-10 grid grid-cols-1 lg:grid-cols-[1.6fr_1fr] gap-6 px-8 pb-8">
        {/* IZQUIERDA — verificador */}
        <div className={`relative rounded-3xl overflow-hidden transition-colors duration-500 ${result ? (authorized ? 'bg-emerald-600' : 'bg-red-600') : 'bg-white/5 backdrop-blur border border-white/10'}`}>
          <div className="p-8 min-h-[420px] flex flex-col items-center justify-center text-center">
            {!result ? (
              <>
                <h1 className="text-5xl font-black text-white mb-3">Buen provecho</h1>
                <p className="text-lg text-slate-300 mb-8">Ingresa tu RUT para registrar tu ingreso al casino.</p>
                <form onSubmit={submit} className="flex gap-3 w-full max-w-xl">
                  <input ref={inputRef} value={rut} onChange={e => setRut(e.target.value)} autoFocus
                    onBlur={e => { if (!result) setTimeout(() => e.target.focus(), 10); }}
                    placeholder="12.345.678-9"
                    className="flex-1 px-6 py-5 text-3xl font-bold text-center bg-white text-slate-900 rounded-2xl focus:outline-none focus:ring-4 focus:ring-emerald-400 placeholder-slate-300"
                    data-testid="kiosko-rut" />
                  <button type="submit" className="px-8 py-5 bg-emerald-500 hover:bg-emerald-600 text-white text-2xl font-black rounded-2xl transition-colors" data-testid="kiosko-submit">
                    →
                  </button>
                </form>
              </>
            ) : authorized ? (
              <div className="text-white w-full">
                <CheckCircle2 className="w-32 h-32 mx-auto mb-3" strokeWidth={2.5} />
                <h1 className="text-6xl font-black mb-3">AUTORIZADO</h1>
                {result.hab && (
                  <div className="bg-white/15 backdrop-blur rounded-2xl px-6 py-4 max-w-lg mx-auto">
                    <div className="text-xl font-bold">{result.hab.nombre}</div>
                    <div className="text-sm opacity-80">{result.hab.rut} · {result.hab.empresa_nombre || '—'}</div>
                    {result.servicio && (
                      <div className="mt-3 pt-3 border-t border-white/20 flex items-center justify-center gap-4">
                        <div><span className="text-[10px] font-bold uppercase tracking-widest opacity-80">Consumo</span><div className="text-lg font-black">{result.servicio.nombre}</div></div>
                      </div>
                    )}
                  </div>
                )}
                <p className="mt-6 text-sm opacity-80">Pase adelante — la pantalla se limpiará en unos segundos.</p>
              </div>
            ) : (
              <div className="text-white w-full">
                <XCircle className="w-32 h-32 mx-auto mb-3" strokeWidth={2.5} />
                <h1 className="text-6xl font-black mb-3">NO AUTORIZADO</h1>
                <p className="text-2xl mb-4 opacity-90">{result.motivo}</p>
                {result.hab?.nombre && (
                  <div className="bg-white/15 backdrop-blur rounded-2xl px-4 py-2 inline-block">
                    <div className="text-base font-bold">{result.hab.nombre}</div>
                    <div className="text-xs opacity-80">{result.hab.rut}</div>
                  </div>
                )}
                <p className="mt-6 text-sm opacity-80">Contacta a Servicios Generales.</p>
              </div>
            )}
          </div>
        </div>

        {/* DERECHA — últimos ingresos */}
        <div className="rounded-3xl bg-white/5 backdrop-blur border border-white/10 p-6 min-h-[420px]">
          <div className="flex items-center justify-between mb-4">
            <div className="text-white text-lg font-black">Últimos ingresos</div>
            <span className="text-[10px] font-bold uppercase tracking-widest text-emerald-300">
              {state.ultimos_usos.length} de 5
            </span>
          </div>
          {state.ultimos_usos.length === 0 ? (
            <div className="text-slate-400 text-sm text-center py-16">Sin registros aún.</div>
          ) : (
            <ul className="space-y-2">
              {state.ultimos_usos.map((u, idx) => {
                const highlighted = result?.uso_id && u.id === result.uso_id;
                return (
                  <li key={u.id}
                    className={`rounded-xl px-3 py-2.5 flex items-center gap-3 transition-all ${highlighted ? 'bg-emerald-400 text-slate-900 shadow-2xl ring-2 ring-white scale-105' : 'bg-white/5 text-white hover:bg-white/10'}`}
                    data-testid={`kiosko-uso-${idx}`}>
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center font-black text-sm shrink-0 ${highlighted ? 'bg-white text-emerald-800' : 'bg-emerald-500/30 text-emerald-200'}`}>
                      {(u.nombre_trabajador || '?').split(' ').slice(0, 2).map(w => w[0]).join('').toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className={`font-bold truncate ${highlighted ? 'text-lg' : 'text-sm'}`}>
                        {u.nombre_trabajador || u.rut_trabajador}
                      </div>
                      <div className={`text-[11px] truncate ${highlighted ? 'text-slate-700' : 'opacity-70'}`}>
                        {(u.empresa_nombre || '').slice(0, 28)}
                      </div>
                    </div>
                    <div className={`text-right shrink-0 ${highlighted ? 'text-slate-800' : 'text-emerald-300'}`}>
                      <div className="text-[11px] font-bold uppercase tracking-widest opacity-80">
                        {u.comida}
                      </div>
                      <div className={`font-mono ${highlighted ? 'text-lg font-black text-slate-900' : 'text-xs opacity-70'}`}>
                        {u.hora}
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>

      {/* Footer */}
      <div className="relative z-10 text-center pb-4">
        <p className="text-[10px] uppercase tracking-widest text-white/40">
          © TystaCore · Compañía Minera Lomas Bayas
        </p>
      </div>
    </div>
  );
}
